import type { Account, AccountRoleAssignment } from "../../services/financialService";
import type { ExportColumn } from "../../utils/exportTable";

export type AccountType = Account["account_type"];

export const ACCOUNT_TYPES: { type: AccountType; label: string; plural: string; hint: string; dot: string }[] = [
  { type: "asset", label: "Asset", plural: "Assets", hint: "What the school owns or is owed — cash, bank, fees receivable, equipment.", dot: "bg-sky-500" },
  { type: "liability", label: "Liability", plural: "Liabilities", hint: "What the school owes — suppliers, staff pay, taxes, money held for families.", dot: "bg-orange-500" },
  { type: "equity", label: "Equity", plural: "Equity", hint: "The school's own fund — surpluses kept over the years.", dot: "bg-violet-500" },
  { type: "income", label: "Income", plural: "Income", hint: "What the school earns — fees, canteen and uniform sales, grants.", dot: "bg-emerald-500" },
  { type: "expense", label: "Expense", plural: "Expenses", hint: "What the school spends — salaries, supplies, utilities, depreciation.", dot: "bg-rose-500" },
];

export const TYPE_DOTS = Object.fromEntries(ACCOUNT_TYPES.map((entry) => [entry.type, entry.dot])) as Record<AccountType, string>;

/** Where an account stands, in the bursar's words rather than the workflow's. */
export type AccountStanding = "in_use" | "inactive" | "awaiting" | "draft" | "returned" | "rejected";

export const ACCOUNT_STANDINGS: Record<AccountStanding, { label: string; dot: string }> = {
  in_use: { label: "In use", dot: "bg-green-500" },
  inactive: { label: "Inactive", dot: "bg-gray-400" },
  awaiting: { label: "Awaiting approval", dot: "bg-blue-500" },
  draft: { label: "Not yet submitted", dot: "bg-gray-400" },
  returned: { label: "Sent back", dot: "bg-orange-500" },
  rejected: { label: "Rejected", dot: "bg-red-500" },
};

export function accountStanding(account: Account): AccountStanding {
  switch (account.workflow_status) {
    case "approved":
      return account.is_active ? "in_use" : "inactive";
    case "submitted":
    case "verified":
      return "awaiting";
    case "returned":
      return "returned";
    case "rejected":
      return "rejected";
    default:
      return "draft";
  }
}

export const accountPurpose = (account: Account) =>
  account.is_control_account ? "control" : account.is_reporting_account ? "summary" : "posting";

export const PURPOSE_LABELS: Record<string, string> = {
  posting: "Posting",
  summary: "Summary only",
  control: "Control",
};

export type ChartFilters = {
  search: string;
  type: "" | AccountType;
  standing: "" | AccountStanding;
  purpose: "" | "posting" | "summary" | "control";
};

export const EMPTY_CHART_FILTERS: ChartFilters = { search: "", type: "", standing: "", purpose: "" };

export const CHART_PAGE_SIZES = [25, 50, 100];

export const activeChartFilterCount = (filters: ChartFilters) =>
  [filters.search.trim(), filters.type, filters.standing, filters.purpose].filter(Boolean).length;

const byCode = (a: Account, b: Account) =>
  a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: "base" });

/** The chart in reading order: each type together, each main account followed
 *  by its subaccounts, with how deep each one sits and the names above it. */
export function chartRows(accounts: Account[]) {
  const known = new Set(accounts.map((account) => account.id));
  const childrenOf = new Map<number | "root", Account[]>();
  for (const account of accounts) {
    const key = account.parent && known.has(account.parent) && account.parent !== account.id ? account.parent : "root";
    childrenOf.set(key, [...(childrenOf.get(key) ?? []), account]);
  }
  const rows: { account: Account; depth: number; path: string[]; children: number }[] = [];
  const walk = (key: number | "root", depth: number, path: string[], seen: Set<number>) => {
    for (const account of [...(childrenOf.get(key) ?? [])].sort(byCode)) {
      if (seen.has(account.id)) continue;
      rows.push({ account, depth, path, children: childrenOf.get(account.id)?.length ?? 0 });
      walk(account.id, depth + 1, [...path, account.name], new Set([...seen, account.id]));
    }
  };
  walk("root", 0, [], new Set());
  const order = ACCOUNT_TYPES.map((entry) => entry.type);
  // Stable: within a type the tree order above is kept.
  return rows.sort((a, b) => order.indexOf(a.account.account_type) - order.indexOf(b.account.account_type));
}

export function filterChartRows(rows: ReturnType<typeof chartRows>, filters: ChartFilters, { ignoreType = false } = {}) {
  const term = filters.search.trim().toLowerCase();
  return rows.filter(({ account, path }) =>
    (ignoreType || !filters.type || account.account_type === filters.type) &&
    (!filters.standing || accountStanding(account) === filters.standing) &&
    (!filters.purpose || accountPurpose(account) === filters.purpose) &&
    (!term ||
      [account.code, account.name, account.description ?? "", ...path].some((value) => value.toLowerCase().includes(term))),
  );
}

export type ChartRow = ReturnType<typeof chartRows>[number];

/** The chart as a spreadsheet: one row per account, its place in the tree spelled out. */
export function chartExportColumns(): ExportColumn<ChartRow>[] {
  return [
    { header: "Code", accessor: ({ account }) => account.code },
    { header: "Account", accessor: ({ account }) => account.name },
    { header: "Under", accessor: ({ path }) => path.join(" › ") },
    { header: "Type", accessor: ({ account }) => ACCOUNT_TYPES.find((entry) => entry.type === account.account_type)?.label ?? account.account_type },
    { header: "Purpose", accessor: ({ account }) => PURPOSE_LABELS[accountPurpose(account)] + (account.is_contra_account ? " (contra)" : "") },
    { header: "Normal balance", accessor: ({ account }) => (account.normal_balance === "debit" ? "Debit" : "Credit") },
    { header: "Statement line", accessor: ({ account }) => account.statement_category_name || "" },
    { header: "Postings", accessor: ({ account }) => account.transaction_count ?? 0 },
    { header: "Standing", accessor: ({ account }) => ACCOUNT_STANDINGS[accountStanding(account)].label },
    { header: "Description", accessor: ({ account }) => account.description || "" },
  ];
}

export function countByType(rows: ReturnType<typeof chartRows>) {
  const counts: Record<string, number> = { "": rows.length };
  for (const { account } of rows) counts[account.account_type] = (counts[account.account_type] ?? 0) + 1;
  return counts;
}

/** Posting roles grouped by how far along they are. */
export type RoleStage = "unassigned" | "in_progress" | "in_use";

export function roleStage(role: AccountRoleAssignment): RoleStage {
  if (role.workflow_status === "approved" && role.account) return "in_use";
  if (role.workflow_status === "unassigned" || role.workflow_status === "rejected") return role.account ? "in_use" : "unassigned";
  return "in_progress";
}

export function roleStageCounts(roles: AccountRoleAssignment[]) {
  const counts: Record<"" | RoleStage, number> = { "": roles.length, unassigned: 0, in_progress: 0, in_use: 0 };
  for (const role of roles) counts[roleStage(role)] += 1;
  return counts;
}
